import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import type {
  TimeCardDetail,
  TimeCardNote as TimeCardNoteType,
  PatchedTimeCardNote,
  PatchedTimeCardStatusUpdate,
  Status,
} from '../../../../../js/api';
import { AssignmentsService, DocumentsService } from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { ActivityLog } from '../../../components/activity-log';
import { Avatar } from '../../../components/avatar';
import { DocumentsTab } from '../../../components/documents-tab';
import { DrawerCloseButton } from '../../../components/drawer';
import { DropdownSelect } from '../../../components/dropdown-select';
import { SectionHeader } from '../../../components/SectionHeader';
import { StatusTransitionModal } from '../../../components/status-transition-modal';
import { StatusPill } from '../../../components/StatusPill';
import { Tabs, TabsButton, TabsContent } from '../../../components/tabs';
import { TooltippedText } from '../../../components/tooltipped-text';
import { MENU_BADGES_QUERY_KEY, QUEUE_NOTIFICATIONS_QUERY_KEY } from '../../../constants/queryKeys';
import { WorkflowScopes } from '../../../constants/workflows';
import { useCollapseOnOverflow } from '../../../hooks/useCollapseOnOverflow';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { StatusMetadata, useWorkflowMetadata } from '../../../hooks/useWorkflowMetadata';
import type { ActivityHistory, ActivityStatusHistory } from '../../../utils/activity-types';
import { formatDuration } from '../../../utils/date';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { READ_ONLY_LABEL_STYLE, READ_ONLY_VALUE_STYLE } from '../../../utils/globalStyles';
import { StatusTransitionButtons } from '../../../utils/statusTransitions';
import { getQueueNameForStatus } from '../../../utils/workflowUtils';
import { ExpensesTab } from './ExpensesTab';
import {
  PROVIDER_TIMECARDS_LIST_QUERY_KEY,
  TIMECARD_CHANGE_HISTORY_QUERY_KEY,
  TIMECARD_DOCUMENTS_COUNT_QUERY_KEY,
  TIMECARD_EXPENSES_QUERY_KEY,
  TIMECARD_NOTES_QUERY_KEY,
  TIMECARD_QUERY_KEY,
  TIMECARD_STATUS_HISTORY_QUERY_KEY,
} from './queryKeys';
import { ShiftsTab } from './ShiftsTab';

export type TimecardDrawerProps = {
  timecardId: string;
  onClose?: () => void;
  prevId?: string | null;
  nextId?: string | null;
  selectedQueueCode?: string | undefined;
  isFullPage?: boolean;
};

/* eslint-disable sonarjs/cognitive-complexity */
export const TimecardDrawer: React.FC<TimecardDrawerProps> = ({
  timecardId,
  onClose,
  prevId,
  nextId,
  selectedQueueCode,
  isFullPage = false,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Provider always has edit access (status-based locking is handled by workflow)
  const canEditTimecard = true;

  const defaultTab = 'shifts';
  const validTabs = ['shifts', 'expenses', 'documents'];
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTabState] = useState(
    tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : defaultTab
  );

  const setActiveTab = useCallback(
    (tab: string) => {
      setActiveTabState(tab);
      const next = new URLSearchParams(searchParams);
      if (tab && tab !== defaultTab) {
        next.set('tab', tab);
      } else {
        next.delete('tab');
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [targetStatusCode, setTargetStatusCode] = useState<string | null>(null);

  const LABEL_STYLE: string = READ_ONLY_LABEL_STYLE;
  const VALUE_STYLE: string = READ_ONLY_VALUE_STYLE;

  const { statuses: workflowStatuses, isLoading: isLoadingWorkflow } = useWorkflowMetadata(
    WorkflowScopes.TimeCard
  );

  // Fetch timecard detail
  const {
    data: timecard,
    isLoading: isLoadingTimecard,
    error: timecardError,
  } = useQuery<TimeCardDetail>({
    queryKey: [TIMECARD_QUERY_KEY, timecardId],
    queryFn: () => AssignmentsService.assignmentsTimecardRetrieve({ id: timecardId }),
    enabled: !!timecardId,
    retry: false,
  });

  // Fetch notes
  const { data: notesData } = useQuery({
    queryKey: [TIMECARD_NOTES_QUERY_KEY, timecardId],
    queryFn: () =>
      AssignmentsService.assignmentsTimecardNotesList({
        id: timecardId,
        ordering: '-created_at',
      }),
    enabled: !!timecardId,
    select: (data: any) => {
      if (!data) return [];
      if (Array.isArray(data)) return data;
      if ('results' in data) return data.results || [];
      return [data];
    },
  });

  // Fetch status history
  const { data: statusHistory } = useQuery({
    queryKey: [TIMECARD_STATUS_HISTORY_QUERY_KEY, timecardId],
    queryFn: () =>
      AssignmentsService.assignmentsTimecardStatusHistoryRetrieve({
        id: timecardId,
        sort: 'desc',
      }),
    enabled: !!timecardId,
  });

  // Fetch change history
  const { data: changeHistory } = useQuery({
    queryKey: [TIMECARD_CHANGE_HISTORY_QUERY_KEY, timecardId],
    queryFn: () =>
      AssignmentsService.assignmentsTimecardChangeHistoryRetrieve({
        id: timecardId,
        sort: 'desc',
      }),
    enabled: !!timecardId,
  });

  // Tab badge counts
  const { data: expensesData } = useQuery({
    queryKey: [TIMECARD_EXPENSES_QUERY_KEY, timecardId],
    queryFn: () => AssignmentsService.assignmentsTimecardExpensesList({ id: timecardId }),
    enabled: !!timecardId,
  });
  const expenseCount = Array.isArray(expensesData) ? expensesData.length : 0;

  const { data: documentsData } = useQuery({
    queryKey: [TIMECARD_DOCUMENTS_COUNT_QUERY_KEY, timecardId],
    queryFn: () =>
      DocumentsService.documentsList({
        contextType: WorkflowScopes.TimeCard,
        contextId: timecardId,
        limit: 1,
      }),
    enabled: !!timecardId,
  });
  const documentCount = (documentsData as any)?.count ?? 0;

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [TIMECARD_QUERY_KEY, timecardId] });
    queryClient.invalidateQueries({ queryKey: [TIMECARD_NOTES_QUERY_KEY, timecardId] });
    queryClient.invalidateQueries({ queryKey: [TIMECARD_STATUS_HISTORY_QUERY_KEY, timecardId] });
    queryClient.invalidateQueries({ queryKey: [TIMECARD_CHANGE_HISTORY_QUERY_KEY, timecardId] });
    queryClient.invalidateQueries({ queryKey: [TIMECARD_EXPENSES_QUERY_KEY, timecardId] });
    queryClient.invalidateQueries({
      queryKey: [TIMECARD_DOCUMENTS_COUNT_QUERY_KEY, timecardId],
    });
    queryClient.invalidateQueries({ queryKey: [PROVIDER_TIMECARDS_LIST_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [MENU_BADGES_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY] });
  }, [queryClient, timecardId]);

  const checkAndNavigateIfRemoved = useCallback(
    (updated?: TimeCardDetail) => {
      if (!selectedQueueCode || selectedQueueCode === 'all') return;
      const curStatusCode = updated?.current_status?.code ?? timecard?.current_status?.code;
      const queueName = getQueueNameForStatus(workflowStatuses, curStatusCode);

      if (queueName && queueName !== selectedQueueCode) {
        const targetId = nextId || prevId || null;
        const nextParams = new URLSearchParams(searchParams);
        if (targetId) {
          nextParams.set('id', String(targetId));
        } else {
          nextParams.delete('id');
        }
        setSearchParams(nextParams, { replace: true });
        if (!targetId) onClose?.();
      }
    },
    [
      selectedQueueCode,
      timecard,
      workflowStatuses,
      nextId,
      prevId,
      searchParams,
      setSearchParams,
      onClose,
    ]
  );

  const statusUpdateMutation = useMutation({
    mutationFn: (data: PatchedTimeCardStatusUpdate) =>
      AssignmentsService.assignmentsTimecardStatusUpdatePartialUpdate({
        id: timecardId,
        requestBody: data,
      }),
    onSuccess: (updated) => {
      toast.success('Status updated');
      invalidateQueries();
      checkAndNavigateIfRemoved(updated as TimeCardDetail);
    },
    onError: (error) => handleApiError(error),
  });

  const handleStatusUpdate = useCallback(
    (data: { statusCode: string | null; reasonCode: string | null; comment?: string }) => {
      statusUpdateMutation.mutate({
        code: data.statusCode || undefined,
        reason_code: data.reasonCode,
        comment: data.comment,
      });
      setIsStatusModalOpen(false);
    },
    [statusUpdateMutation]
  );

  const handleStatusClick = useCallback((status: Status) => {
    setTargetStatusCode(status.code);
    setIsStatusModalOpen(true);
  }, []);

  const tabs = useMemo(
    () => [
      { code: 'shifts', label: 'Shifts' },
      {
        code: 'expenses',
        label: expenseCount > 0 ? `Expenses (${expenseCount})` : 'Expenses',
      },
      {
        code: 'documents',
        label: documentCount > 0 ? `Documents (${documentCount})` : 'Documents',
      },
    ],
    [expenseCount, documentCount]
  );

  const { ref: tabsMeasureRef, collapsed: isOverflowCollapsed } =
    useCollapseOnOverflow<HTMLElement>({
      observeChildren: true,
      forceCollapse: isMobile,
      constraintRef: drawerRef,
      recheckOn: { id: timecardId, tabs },
    });

  if (isLoadingTimecard) {
    return <div className="tw:p-4 tw:text-center tw:text-zinc-500">Loading timecard...</div>;
  }

  if (timecardError || !timecard) {
    return (
      <div className="tw:p-4 tw:text-center tw:text-stoplight-danger tw:bg-stoplight-danger/10 tw:my-2 tw:rounded-lg">
        Failed to load timecard.
      </div>
    );
  }

  const currentStatusCode = timecard.current_status?.code ?? undefined;

  return (
    <div ref={drawerRef} className="tw:flex tw:flex-col tw:mt-4">
      {/* Header */}
      <div className="tw:border-b tw:border-zinc-200 tw:pb-4">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
          <Avatar
            src={timecard.provider?.profile_picture || undefined}
            className="tw:size-12 tw:shrink-0 tw:rounded-lg"
            square={true}
          />

          <div className="tw:flex-1 tw:min-w-0">
            <div className="tw:text-lg tw:font-semibold">{timecard.facility?.name}</div>
            <div className="tw:text-xs tw:text-zinc-500 tw:font-mono tw:flex tw:items-center tw:gap-1">
              {timecard.code}
              <TooltippedText
                tooltipContent="Copy code"
                tooltipClassName="tw:bg-brand-green-2 tw:text-zinc-700"
                placement="bottom"
              >
                <button
                  type="button"
                  className="tw:text-zinc-600 tw:hover:text-zinc-600 tw:bg-transparent tw:border-none tw:p-0 tw:cursor-pointer"
                  onClick={() => {
                    navigator.clipboard.writeText(timecard.code || '');
                    toast.success('Copied', { autoClose: 1500 });
                  }}
                >
                  <i className="far fa-copy tw:text-[10px]" />
                </button>
              </TooltippedText>
            </div>
          </div>

          {!isFullPage && !isMobile && (
            <button
              type="button"
              className="tw:text-zinc-400 tw:hover:text-zinc-600 tw:p-1"
              onClick={() => {
                const tabParam = activeTab !== defaultTab ? `?tab=${activeTab}` : '';
                navigate(`/app/provider/timecards/${timecardId}${tabParam}`, {
                  state: { referrer: window.location.pathname + window.location.search },
                });
              }}
              title="Open full page"
            >
              <i className="fas fa-up-right-from-square" />
            </button>
          )}
          <DrawerCloseButton onClick={() => onClose?.()} />
        </div>

        <div className="tw:mt-2 tw:text-sm tw:text-zinc-600 tw:flex tw:flex-wrap tw:gap-x-4">
          <span>
            <i className="fas fa-map-marker-alt tw:text-zinc-400 tw:mr-1" />
            {getFacilityLocationDisplayValue(timecard.facility)}
          </span>
        </div>

        <div className="tw:flex tw:gap-2 tw:items-start tw:shrink-0 tw:mt-2 tw:flex-wrap">
          <StatusPill
            scope={WorkflowScopes.TimeCard}
            status={timecard.current_status || 'Unknown'}
          />
        </div>

        <dl className="tw:grid tw:grid-cols-3 tw:gap-2 tw:text-sm tw:!mt-3 tw:!mb-0">
          <div>
            <dt className={LABEL_STYLE}>Shifts</dt>
            <dd className={VALUE_STYLE}>
              {timecard.active_shift_count} active
              {timecard.removed_shift_count > 0 && (
                <span className="tw:text-zinc-400"> / {timecard.removed_shift_count} removed</span>
              )}
            </dd>
          </div>
          <div>
            <dt className={LABEL_STYLE}>Duration</dt>
            <dd className={VALUE_STYLE}>
              {timecard.total_duration ? formatDuration(timecard.total_duration) : '--'}
            </dd>
          </div>
        </dl>

        <div className="tw:mt-3 tw:flex tw:flex-wrap tw:gap-2">
          <StatusTransitionButtons
            isLoading={isLoadingWorkflow}
            currentStatusCode={currentStatusCode}
            workflowMetadata={workflowStatuses as StatusMetadata}
            onStatusUpdate={(status: Status) => statusUpdateMutation.mutate({ code: status.code })}
            onStatusClick={handleStatusClick}
            isPending={statusUpdateMutation.isLoading}
            small={isMobile}
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs activeTab={activeTab} onChange={setActiveTab}>
        {isOverflowCollapsed ? (
          <div className="tw:pb-2 tw:px-1 tw:pt-1">
            <div className="tw:text-xs tw:text-zinc-500 tw:mb-1">Select View</div>
            <DropdownSelect
              inputId="timecard-drawer-section"
              aria-label="Timecard section"
              allOptions={tabs.map((t) => ({ value: t.code, label: t.label }))}
              value={activeTab}
              isSearchable={false}
              isClearable={false}
              onChange={(opt: any) => {
                if (opt && !Array.isArray(opt)) setActiveTab(opt.value);
              }}
            />
          </div>
        ) : (
          <div className="tw:border-b tw:border-zinc-200">
            <div
              ref={tabsMeasureRef as React.RefObject<HTMLDivElement>}
              className="tw:flex tw:gap-1 tw:overflow-hidden"
            >
              {tabs.map((tab) => (
                <TabsButton key={tab.code} tab={tab.code}>
                  {tab.label}
                </TabsButton>
              ))}
            </div>
          </div>
        )}

        <div className="tw:flex-1 tw:overflow-y-auto">
          <TabsContent tab="shifts">
            <ShiftsTab
              timecard={timecard}
              canEdit={canEditTimecard}
              onSuccess={invalidateQueries}
            />
          </TabsContent>

          <TabsContent tab="expenses">
            <ExpensesTab
              timecard={timecard}
              canEdit={canEditTimecard}
              ownerId={timecard.provider?.id || ''}
              relatedProviderId={timecard.provider?.id}
            />
          </TabsContent>

          <TabsContent tab="documents">
            <DocumentsTab
              contextType={WorkflowScopes.TimeCard}
              contextId={timecardId}
              ownerId={timecard.provider?.id || ''}
              relatedProviderId={timecard.provider?.id}
            />
          </TabsContent>

          <div className="tw:mt-6 tw:p-2 tw:bg-brand-green-3 tw:rounded-lg">
            <SectionHeader className="tw:mb-2 tw:p-1 tw:text-brand-green-0" headerText="Activity" />
            <ActivityLog
              enableCommentsFilter={true}
              enableStatusChangesFilter={true}
              enableUpdatesFilter={true}
              enableEventsFilter={false}
              composerPlaceholder="Enter a comment (visible to all parties)..."
              notes={notesData}
              statusHistory={statusHistory as ActivityStatusHistory[] | undefined}
              changeHistory={changeHistory as ActivityHistory[] | undefined}
              noteMutations={{
                createNote: async (data) => {
                  await AssignmentsService.assignmentsTimecardNotesCreate({
                    id: timecardId,
                    requestBody: {
                      comment: data.comment,
                      time_card: timecardId,
                    } as TimeCardNoteType,
                  });
                },
                updateNote: async (noteId: string, data: PatchedTimeCardNote) => {
                  return AssignmentsService.assignmentsTimecardNotePartialUpdate({
                    id: noteId,
                    requestBody: data,
                  });
                },
                deleteNote: async (noteId: string) => {
                  await AssignmentsService.assignmentsTimecardNoteDestroy({
                    id: noteId,
                  });
                },
                notesQueryKey: [TIMECARD_NOTES_QUERY_KEY, timecardId],
                extraInvalidateQueryKeys: [[TIMECARD_STATUS_HISTORY_QUERY_KEY, timecardId]],
              }}
            />
          </div>
        </div>
      </Tabs>

      <StatusTransitionModal
        open={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        onConfirm={handleStatusUpdate}
        initialStatusCode={currentStatusCode ?? null}
        targetStatusCode={targetStatusCode}
        workflowScope={WorkflowScopes.TimeCard}
        isPending={statusUpdateMutation.isLoading}
        currentReasonCode={null}
        currentStatusTitle={timecard.current_status?.title ?? null}
      />
    </div>
  );
};
